import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { getAuth, createUserWithEmailAndPassword, signInWithEmailAndPassword } from '../../auth/staffAuthApi';
import { app } from '../../auth/appAuth';
import { useAuth } from '../../context/AuthContext';
import { sendCustomVerificationEmail } from '../../auth/api/emailVerificationApi';
import { acceptSpecialAccessInvite, getSpecialAccessInviteByToken } from '../services/specialAccessApi';
import AuthLoadingState from '../../components/loading/AuthLoadingState';
import '../../auth/styles/invite.css';

export default function SpecialAccessInviteAcceptPage() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [invite, setInvite] = useState(null);
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [companyName, setCompanyName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [pwScore, setPwScore] = useState(0);
  const [showPw, setShowPw] = useState(false);
  const [showPw2, setShowPw2] = useState(false);
  const [loading, setLoading] = useState(false);
  const [loadingState, setLoadingState] = useState('initial');
  const [error, setError] = useState('');
  const token = searchParams.get('token') || '';

  useEffect(() => {
    if (!token) {
      setError('Invalid or missing invite token');
      return;
    }

    const load = async () => {
      try {
        const data = await getSpecialAccessInviteByToken(token);
        setInvite(data);
        setEmail(data.email || '');
      } catch (err) {
        setError(err?.message || 'Invalid invite');
      }
    };

    load();
  }, [token]);

  useEffect(() => {
    const value = password || '';
    let score = 0;
    if (value.length >= 6) score += 1;
    if (/[A-Z]/.test(value) && /[a-z]/.test(value)) score += 1;
    if (/\d/.test(value)) score += 1;
    if (/[^A-Za-z0-9]/.test(value) || value.length >= 12) score += 1;
    setPwScore(score);
  }, [password]);

  const handleAccept = async () => {
    if (!email || !password) {
      setError('Email and password are required.');
      return;
    }
    if (password.length < 6) {
      setError('Password must be at least 6 characters.');
      return;
    }
    if (password !== confirmPassword) {
      setError('Passwords do not match.');
      return;
    }
    if (!companyName.trim()) {
      setError('Company name is required.');
      return;
    }

    try {
      setLoading(true);
      setLoadingState('verifying');
      setError('');
      const auth = getAuth(app);

      if (user && user.email && email && user.email.toLowerCase() !== email.toLowerCase()) {
        setError('You are signed in as a different user. Please sign out and sign in with the invited email.');
        setLoadingState('error');
        return;
      }

      let currentUser = auth.currentUser;
      if (!currentUser) {
        try {
          const created = await createUserWithEmailAndPassword(auth, email, password);
          currentUser = created.user;
        } catch (createErr) {
          if (createErr?.code === 'auth/email-already-in-use') {
            const signIn = await signInWithEmailAndPassword(auth, email, password);
            currentUser = signIn.user;
          } else {
            throw createErr;
          }
        }
      }

      if (!currentUser || (currentUser.email && email && currentUser.email.toLowerCase() !== email.toLowerCase())) {
        setError('Authenticated user does not match the invited email.');
        setLoadingState('error');
        return;
      }

      setLoadingState('loading');
      const idToken = await currentUser.getIdToken();

      const acceptPromise = acceptSpecialAccessInvite({ token, idToken, firstName, lastName, companyName });
      void sendCustomVerificationEmail(idToken).catch((verifyErr) => {
        console.warn('Could not send verification email:', verifyErr);
      });

      await acceptPromise;
      setLoadingState('finalizing');
      window.location.replace('/app/overview');
    } catch (err) {
      setError(err?.message || 'Failed to accept invite');
      setLoadingState('error');
    }
  };

  const resetLoadingState = () => {
    setLoading(false);
    setLoadingState('initial');
    setError('');
  };

  const handleCancel = () => {
    if (loading) return;
    const confirmed = window.confirm('Are you sure you want to close? You can accept this invitation later from the email link.');
    if (!confirmed) return;

    try {
      if (window.opener || window.top !== window.self) {
        window.close();
      }
      window.open('', '_self');
      window.close();
    } catch (err) {
      // Ignore close errors.
    }

    if (document.referrer && document.referrer !== '') {
      navigate(-1);
      return;
    }

    if (user) {
      navigate('/app/overview');
    } else {
      navigate('/app/login');
    }
  };

  const pwLabel = ['Too weak', 'Weak', 'Fair', 'Good', 'Strong'][pwScore];
  const pwClass = ['weak', 'weak', 'fair', 'good', 'strong'][pwScore];

  if (loading) {
    return (
      <div className="invite-page">
        <div className="invite-card invite-card--loading">
          <AuthLoadingState
            state={loadingState}
            onReset={resetLoadingState}
            customMessage={
              loadingState === 'verifying' ? 'Creating Your Account...' :
              loadingState === 'loading' ? 'Setting Up Your Company...' :
              loadingState === 'finalizing' ? 'Almost There...' :
              loadingState === 'error' ? 'Account Creation Failed' :
              'Processing...'
            }
            customSubMessage={
              loadingState === 'verifying' ? 'Please wait while we create your account' :
              loadingState === 'loading' ? 'Setting up your company profile and permissions' :
              loadingState === 'finalizing' ? 'Redirecting you to your dashboard' :
              loadingState === 'error' ? (error || 'There was a problem creating your account') :
              'Please wait...'
            }
          />
        </div>
      </div>
    );
  }

  return (
    <div className="invite-page">
      <div className="invite-card">
        <div className="invite-card__header" style={{ flexDirection: 'column', justifyContent: 'center', textAlign: 'center' }}>
          <img src="/neatschedule.png" alt="Neat Schedule Logo" className="invite-logo-img" />
          <div>
            <div className="invite-title">Special Access Invite</div>
            <div className="invite-subtitle">Create your Neat Schedule account</div>
          </div>
        </div>
        <div className="invite-card__body" style={{ overflow: 'visible' }}>
          <div className="invite-banner">
            You were invited as a special access user.
          </div>
          <div className="invite-field">
            <label className="invite-label">Email</label>
            <input className="invite-input" type="email" value={email} readOnly />
          </div>
          <div className="invite-field">
            <label className="invite-label">Password
              <span className="info-dot" title="How to reach Strong">
                i
                <span className="tooltip">
                  Use <b>12+ characters</b> with a mix of <b>upper/lowercase</b>, <b>numbers</b>, and <b>symbols</b>.
                </span>
              </span>
            </label>
            <div className="invite-input-wrap">
              <input className="invite-input" type={showPw ? 'text' : 'password'} value={password} onChange={(e) => setPassword(e.target.value)} />
              <button type="button" className="toggle-eye" onClick={() => setShowPw((s) => !s)}>{showPw ? 'Hide' : 'Show'}</button>
            </div>
            <div className="pw-meter">
              <div className="pw-meter__bars">
                {[0, 1, 2, 3].map((i) => (
                  <div key={i} className={`pw-meter__bar ${pwScore > i ? `active ${pwClass}` : ''}`} />
                ))}
              </div>
              <div className="pw-meter__label">{pwLabel}</div>
            </div>
          </div>
          <div className="invite-field">
            <label className="invite-label">Re-enter Password</label>
            <div className="invite-input-wrap">
              <input
                className="invite-input"
                type={showPw2 ? 'text' : 'password'}
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                style={password && confirmPassword && password !== confirmPassword ? { borderColor: '#dc2626' } : undefined}
              />
              <button type="button" className="toggle-eye" onClick={() => setShowPw2((s) => !s)}>{showPw2 ? 'Hide' : 'Show'}</button>
            </div>
            {password && confirmPassword && password !== confirmPassword && (
              <div className="invite-error">Passwords do not match</div>
            )}
          </div>
          <div className="invite-row">
            <div className="invite-field">
              <label className="invite-label">First Name</label>
              <input className="invite-input" value={firstName} onChange={(e) => setFirstName(e.target.value)} />
            </div>
            <div className="invite-field">
              <label className="invite-label">Last Name</label>
              <input className="invite-input" value={lastName} onChange={(e) => setLastName(e.target.value)} />
            </div>
          </div>
          <div className="invite-field">
            <label className="invite-label">Company Name</label>
            <input
              className="invite-input"
              value={companyName}
              onChange={(e) => setCompanyName(e.target.value)}
              placeholder="Your company name"
            />
          </div>
          {error && <div className="invite-error">{error}</div>}
        </div>
        <div className="invite-card__footer">
          <div className="invite-actions">
            <button className="btn btn-outline" onClick={handleCancel}>Close</button>
            <button
              className="btn btn-primary"
              onClick={handleAccept}
              disabled={!token || !email || !password || password !== confirmPassword || !companyName.trim()}
            >
              Create Account
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
